// Outstanding LOCAL database operations (#4401).
//
// A boot stalls, and every path that could have reported it is silent: a promise
// that never settles produces no rejection, so a try/catch never sees it, and
// none of these paths log on success. This module records when a local database
// operation is issued and when it settles, so a stalled-boot payload can name
// the one that never came back and how long it has been outstanding.
//
// It is REPORT-ONLY. It never abandons an operation, never rejects, and never
// changes a fallback. Adding timeouts here was considered and rejected.
//
// The full argument — which two await chains stall together, why the payload
// could not say so before, and why timeouts would not have helped — is in
// design-docs/boot-sequence.md, "Naming the step that is wedged (#4401)". Read
// it there rather than restating it here; one copy cannot drift from itself.
//
// Keep this module free of Angular and browser-only imports — the unit test
// loads it under plain node, and the clock is injected so tests drive time by
// hand. Same pattern as app/splash-watchdog.ts and app/boot-timings.ts.

/**
 * How many outstanding operations are remembered at once.
 *
 * A genuinely wedged store means operations are issued and never settle, so this
 * map would otherwise grow for as long as the app stays open.
 *
 * Past this many, the NEWEST arrival is refused rather than recorded, and the
 * oldest entries are kept. That direction is the whole point: `longestOutstanding()`
 * reports the OLDEST operation, because when a store stops answering the first
 * stuck call is the cause and everything after it is queued behind it. Evicting
 * the oldest — the obvious implementation — would throw away precisely the entry
 * this module exists to report, and the payload would then name some younger
 * operation with a much smaller age. That is not a smaller signal, it is a wrong
 * one.
 *
 * Refusals are counted and reported, so a reader can always tell the difference
 * between "3 operations outstanding" and "3 recorded, 4000 more refused".
 */
export const LOCAL_OP_TRACKER_CAPACITY = 64;

/** One operation that has been issued and has not settled yet. */
export interface OutstandingLocalOp {
  /** What was issued, e.g. `localDb.get:_design/view`. Never a document body. */
  name: string;
  /** How long it has been outstanding, in milliseconds. */
  ageMs: number;
}

export class LocalOpTracker {
  // handle -> { name, startedAt }. Nothing here relies on Map order: entries are
  // only ever removed by handle, and `longestOutstanding()` compares `startedAt`
  // rather than trusting position. At capacity `begin()` refuses the new arrival
  // and keeps what it already holds — there is no eviction.
  private inFlight = new Map<number, { name: string; startedAt: number }>();
  private nextHandle = 1;
  // how many begin() calls were refused because the map was full, since the last
  // reset(). Reported alongside the count so a full tracker is never mistaken
  // for a quiet one — see LOCAL_OP_TRACKER_CAPACITY.
  private refusedCount = 0;

  constructor(private readonly now: () => number = () => performance.now()) {}

  /**
   * Record that a local operation has been issued. Returns a handle to pass to
   * `end`. The name should identify the CALL, not its data — it travels to
   * telemetry, so it must never carry a document body or a user value.
   *
   * At capacity the new arrival is REFUSED and counted, keeping the older
   * entries (see LOCAL_OP_TRACKER_CAPACITY for why that direction matters). The
   * returned handle is still valid to pass to `end` — it simply matches nothing.
   */
  begin(name: string): number {
    const handle = this.nextHandle++;
    if (this.inFlight.size >= LOCAL_OP_TRACKER_CAPACITY) {
      this.refusedCount++;
      return handle;
    }
    this.inFlight.set(handle, { name, startedAt: this.now() });
    return handle;
  }

  /**
   * Record that the operation settled, however it settled. Callers put this in a
   * `finally`, so it must tolerate a handle it never held — refused on arrival
   * because the tracker was full, cleared by `reset`, or ended twice on a
   * torn-down path. An unknown
   * handle is a legitimate no-op here, not a swallowed error: there is no
   * information in it to lose, and throwing would turn a real failure into a
   * crash on the path that is already failing.
   */
  end(handle: number): void {
    this.inFlight.delete(handle);
  }

  /** How many operations are outstanding right now. */
  outstandingCount(): number {
    return this.inFlight.size;
  }

  /** How many `begin` calls were refused at capacity since the last reset. */
  refusedAtCapacityCount(): number {
    return this.refusedCount;
  }

  /**
   * The operation that has been waiting longest, or undefined if none is.
   *
   * Longest, not most recent: when a store stops answering, later operations
   * queue behind the first stuck one, so the oldest is the cause and the newer
   * ones are its symptoms.
   */
  longestOutstanding(): OutstandingLocalOp | undefined {
    let oldest: { name: string; startedAt: number } | undefined = undefined;
    for (const op of this.inFlight.values()) {
      if (!oldest || op.startedAt < oldest.startedAt) {
        oldest = op;
      }
    }
    if (!oldest) return undefined;
    return { name: oldest.name, ageMs: this.now() - oldest.startedAt };
  }

  /**
   * Flat telemetry properties for the hang payload. Empty when nothing is
   * outstanding and nothing was ever refused. Empty rather than zeroed on
   * purpose: an absent property reads as "nothing was waiting", which is a
   * different fact from "something was waiting for 0 ms".
   *
   * The refusal count is reported on its own even when nothing is outstanding
   * any more. Otherwise a run that hit the ceiling and then settled would
   * report a completely quiet tracker, which is the exact misreading the count
   * exists to prevent.
   */
  snapshot(): Record<string, string | number> {
    // only when it happened — an absent property reads as "the tracker saw
    // everything", which is a different fact from "it refused none this time"
    const refused = this.refusedCount > 0 ? { local_ops_refused_count: this.refusedCount } : {};
    const outstanding = this.longestOutstanding();
    if (!outstanding) return refused;
    return {
      local_op_outstanding: outstanding.name,
      local_op_outstanding_ms: Math.round(outstanding.ageMs),
      local_ops_outstanding_count: this.outstandingCount(),
      ...refused,
    };
  }

  /**
   * Forget everything. Called once per page load, at the top of
   * `appBootSequenceAsync`, beside `bootTimings.reset()`.
   *
   * Be precise about what that does and does not cover, because the two are
   * easy to confuse. `appBootSequenceAsync` runs once per page load, and a page
   * load gives this module fresh state anyway — so on the common path this call
   * clears nothing. What it does buy is that the contract is stated in one
   * place and does not depend on module state being fresh.
   *
   * KNOWN LIMITATION, deliberately not fixed here. A boot that restarts INSIDE
   * one page — a warm-resume fallback, or a different user signing in without a
   * reload — does NOT come through here, so an operation left pending against a
   * `DatabaseWrapper` that has since been replaced and closed is never cleared.
   * Such an operation can never settle, so it stays at the front of the list and
   * keeps winning `longestOutstanding()` with an ever-growing age.
   *
   * What makes that survivable rather than misleading: every tracked name
   * carries its database identifier, so a reader can see the report names a
   * database that is no longer the live one. If a real report ever shows this
   * shape, clearing per re-init is the fix — but it must clear only the
   * replaced wrapper's operations, not the shared databases' legitimately
   * in-flight ones, which is more machinery than a report-only tracker has
   * earned so far.
   */
  reset(): void {
    this.inFlight.clear();
    this.refusedCount = 0;
  }
}

/** The one shared tracker for the app (same convention as `bootTimings`). */
export const localOpTracker = new LocalOpTracker();

/**
 * Build the name a local database operation is reported under.
 *
 * The database identifier at the front is load-bearing, not decoration.
 * `commitQueriesAsync` runs for every `DatabaseWrapper`, not just the user one,
 * so several of these can be in flight at the same moment during the
 * shared-database phase — without the prefix a report cannot say WHICH store
 * stopped answering. It is also what makes an operation stranded by an in-page
 * re-init recognisable; see `reset()` above.
 *
 * Every name goes through here so the format lives in exactly one place. Before
 * that it was written out at each call site and kept correct only by a test that
 * read the source files looking for one that had forgotten it.
 *
 * `operation` says what was asked of the store, e.g. `localDb.get:_design/view`.
 */
export function localOpName(databaseId: string, operation: string): string {
  return `${databaseId}:${operation}`;
}

/**
 * Run a local database operation with its lifetime recorded.
 *
 * Wraps the call rather than sitting beside it so the `end` cannot be forgotten
 * on an early return or a throw. It changes nothing about the operation: the
 * same promise is awaited, the same value comes back, and the same error
 * propagates. If the promise never settles — the case this exists for — the
 * entry simply stays outstanding and ages, which is exactly what gets reported.
 */
export async function trackLocalOpAsync<T>(
  name: string,
  run: () => Promise<T>,
  tracker: LocalOpTracker = localOpTracker,
): Promise<T> {
  const handle = tracker.begin(name);
  try {
    return await run();
  } finally {
    tracker.end(handle);
  }
}
